import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ApiService } from '../services/api.service';

export interface OrderLinkingDialogData {
  /** Human-readable label for the order type being linked, e.g. "Sales Order" */
  orderLabel: string;
  /** API endpoint to fetch all candidate orders, e.g. 'salesorder/all' */
  fetchUrl: string;
  /** API endpoint prefix to POST the link — the linked order's id is appended, e.g. 'purchaseorder/5/link-salesorder' */
  linkUrlPrefix: string;
  alreadyLinkedIds: number[];
}

export interface OrderLinkingDialogResult {
  linkedOrder?: any;
}

@Component({
  standalone: true,
  selector: 'app-order-linking-dialog',
  templateUrl: './order-linking-dialog.component.html',
  styleUrl: './order-linking-dialog.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule
  ]
})
export class OrderLinkingDialogComponent implements OnInit {
  orderSearch = new FormControl('');
  orders: any[] = [];
  filteredOrders!: Observable<any[]>;
  selectedOrder: any = null;
  isLinking = false;
  errorMessage = '';

  constructor(
    private dialogRef: MatDialogRef<OrderLinkingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrderLinkingDialogData,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.apiService.get<any[]>(this.data.fetchUrl).subscribe({
      next: (orders) => {
        this.orders = (orders || [])
          .filter(o => !this.data.alreadyLinkedIds.includes(o.id || o.Id))
          .map(o => ({
            id: o.id || o.Id,
            number: o.number || o.Number || '',
            date: o.date || o.Date ? new Date(o.date || o.Date) : null,
            status: o.status || o.Status || '',
            totalAmount: o.totalAmount || o.TotalAmount || 0
          }));
      },
      error: (err) => console.error(`Error loading ${this.data.orderLabel}s:`, err)
    });

    this.filteredOrders = this.orderSearch.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );
  }

  private _filter(value: string): any[] {
    if (typeof value !== 'string') return this.orders;
    const filter = value.toLowerCase();
    return this.orders.filter(o =>
      (o.number || '').toLowerCase().includes(filter) ||
      (o.status || '').toLowerCase().includes(filter)
    );
  }

  displayOrder(o: any): string {
    if (!o) return '';
    return `#${o.number}${o.status ? ' — ' + o.status : ''}`;
  }

  onOrderSelected(o: any): void {
    this.selectedOrder = o;
    this.errorMessage = '';
  }

  clearOrder(): void {
    this.selectedOrder = null;
    this.orderSearch.setValue('');
  }

  link(): void {
    if (!this.selectedOrder) return;
    this.isLinking = true;
    this.errorMessage = '';
    this.apiService.post<any>(`${this.data.linkUrlPrefix}/${this.selectedOrder.id}`, {}).subscribe({
      next: () => {
        this.isLinking = false;
        this.dialogRef.close({ linkedOrder: this.selectedOrder } as OrderLinkingDialogResult);
      },
      error: (err) => {
        this.isLinking = false;
        this.errorMessage = `Failed to link ${this.data.orderLabel.toLowerCase()}. Please try again.`;
        console.error(`Error linking ${this.data.orderLabel}:`, err);
      }
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
